import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sessionFetch } from '@/lib/api/auth';
import { api, ApiError, unwrap } from '@/lib/api/client';
import type { ThreadDetail } from '@/lib/api/types';
import { type AssistantEvent, readSse } from './sse';

export const aiKeys = {
  threads: ['ai', 'threads'] as const,
  thread: (id: string) => ['ai', 'thread', id] as const,
  proposals: ['ai', 'proposals'] as const,
};

export function useThreads(enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.threads,
    queryFn: () => unwrap(api.GET('/api/ai/threads', { params: { query: { limit: 30 } } })),
    enabled,
  });
}

/** How often a thread is re-read while one of its replies is still being written. */
export const WORKING_POLL_MS = 2500;

/** A reply the server is still writing: the page was reloaded mid-answer, or another tab asked. */
export function hasWorkingReply(thread: ThreadDetail | undefined): boolean {
  return !!thread?.messages.some((m) => m.role === 'assistant' && m.status === 'working');
}

/**
 * A stored conversation. While a reply is still being written server-side (and this tab is not
 * streaming it itself — `poll: false`), the thread is re-read every few seconds until the reply
 * is done or failed; the server marks one that died with its process as an error.
 */
export function useThread(id: string | null, { poll = true }: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: aiKeys.thread(id ?? ''),
    queryFn: () => unwrap(api.GET('/api/ai/threads/{thread_id}', { params: { path: { thread_id: id ?? '' } } })),
    enabled: !!id,
    refetchInterval: (query) => (poll && hasWorkingReply(query.state.data) ? WORKING_POLL_MS : false),
  });
}

export async function createThread(scope?: { type: string; id: string }): Promise<string> {
  const t = await unwrap(api.POST('/api/ai/threads', { body: { scope_type: scope?.type ?? null, scope_id: scope?.id ?? null } }));
  return t.id;
}

export function useDeleteThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE('/api/ai/threads/{thread_id}', { params: { path: { thread_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: aiKeys.threads }),
  });
}

/** POST a message and stream the answer (SSE). Resolves when the stream ends. */
export async function streamMessage(
  threadId: string,
  text: string,
  page: Record<string, unknown>,
  onEvent: (e: AssistantEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await sessionFetch(`/api/ai/threads/${threadId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify({ text, page }),
    signal,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: unknown };
    throw new ApiError(res.status, body.detail);
  }
  await readSse(res, onEvent);
}

export function useProposals(enabled: boolean) {
  return useQuery({
    queryKey: aiKeys.proposals,
    queryFn: () => unwrap(api.GET('/api/ai/proposals')),
    enabled,
    refetchInterval: 60_000,
  });
}

export function useDecideProposal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision }: { id: string; decision: 'apply' | 'reject' }) => {
      const call =
        decision === 'apply'
          ? api.POST('/api/ai/proposals/{proposal_id}/apply', { params: { path: { proposal_id: id } } })
          : api.POST('/api/ai/proposals/{proposal_id}/reject', { params: { path: { proposal_id: id } } });
      return unwrap(call);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: aiKeys.proposals });
      void qc.invalidateQueries({ queryKey: ['ai', 'thread'] });
      // An applied action changed data somewhere; let screens refetch.
      void qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== 'ai' && q.queryKey[0] !== 'me' });
    },
  });
}
